// Desktop-only client for the vault: a folder on disk whose `.md` files back
// the note graph. Every call here goes through Rust (see src-tauri/src/lib.rs) —
// the WebView can't read arbitrary paths itself. Guarded by isTauri() at the
// call sites, so the browser build never imports the Tauri APIs.

import { VaultFile, WorkspaceDocument } from "../types";

/** Paths that changed and paths that vanished, as reported by the watcher. */
export interface VaultChange {
  changed: string[];
  removed: string[];
}

/** Prefix marking a note that lives in the vault but isn't open as a tab. */
const VAULT_ID_PREFIX = "vault:";

export const vaultDocumentId = (path: string): string => `${VAULT_ID_PREFIX}${path}`;

/** The file path behind a vault-backed id, or null for a regular tab id. */
export const pathFromDocumentId = (id: string): string | null =>
  id.startsWith(VAULT_ID_PREFIX) ? id.slice(VAULT_ID_PREFIX.length) : null;

/** Last path segment of a vault file, e.g. "notes/ideas.md" -> "ideas.md". */
export const baseName = (path: string): string => path.split(/[\\/]/).pop() ?? path;

/** Join a path onto a root using whichever separator the root already uses. */
export const joinPath = (root: string, segment: string): string => {
  const separator = root.includes("\\") ? "\\" : "/";
  return `${root.replace(/[\\/]+$/, "")}${separator}${segment}`;
};

/** `rel` for a path inside `root`; falls back to the file name when outside it. */
export const relativeTo = (root: string, path: string): string => {
  const normalisedRoot = root.replace(/[\\/]+$/, "");
  const inside = path.toLowerCase().startsWith(`${normalisedRoot.toLowerCase()}`);
  const rel = inside ? path.slice(normalisedRoot.length).replace(/^[\\/]+/, "") : baseName(path);
  return rel.replace(/\\/g, "/");
};

/** Ask for a folder to open as the vault. Returns null when cancelled. */
export const pickVaultFolder = async (): Promise<string | null> => {
  const { open } = await import("@tauri-apps/plugin-dialog");
  const picked = await open({ directory: true, multiple: false, title: "Open folder as vault" });
  return typeof picked === "string" ? picked : null;
};

/** Read every note under `root` into memory. Throws with a message on failure. */
export const scanVault = async (root: string): Promise<VaultFile[]> => {
  const { invoke } = await import("@tauri-apps/api/core");
  return invoke<VaultFile[]>("scan_vault", { path: root });
};

export const readVaultFile = async (path: string): Promise<string> => {
  const { invoke } = await import("@tauri-apps/api/core");
  return invoke<string>("read_vault_file", { path });
};

/** Write markdown back to a path, reusing the export writer in Rust. */
export const writeVaultFile = async (path: string, content: string): Promise<void> => {
  const { invoke } = await import("@tauri-apps/api/core");
  await invoke("save_file", { path, contents: Array.from(new TextEncoder().encode(content)) });
};

export const watchVault = async (root: string): Promise<void> => {
  const { invoke } = await import("@tauri-apps/api/core");
  await invoke("watch_vault", { path: root });
};

export const unwatchVault = async (): Promise<void> => {
  const { invoke } = await import("@tauri-apps/api/core");
  await invoke("unwatch_vault");
};

/** Subscribe to external edits. Resolves to an unlisten function. */
export const onVaultChanged = async (handler: (change: VaultChange) => void): Promise<() => void> => {
  const { listen } = await import("@tauri-apps/api/event");
  return listen<VaultChange>("vault-changed", (event) => handler(event.payload));
};

/**
 * Vault files as documents, so notes.ts (backlinks, tags, name lookup) and the
 * Notes panel treat closed files exactly like open tabs. Files already open are
 * left out — the tab is the live version of that note.
 */
export const vaultDocuments = (files: VaultFile[], openPaths: Set<string>): WorkspaceDocument[] =>
  files
    .filter((file) => !openPaths.has(file.path))
    .map((file) => ({
      id: vaultDocumentId(file.path),
      name: file.name,
      content: file.content,
      updatedAt: file.updatedAt,
      path: file.path,
    }));

/** A folder in the tree, or a file leaf. Built from the files' `rel` paths. */
export interface TreeNode {
  name: string;
  /** Set on files only. */
  path?: string;
  children: TreeNode[];
}

/** Turn flat `rel` paths into a folder tree, folders first then files, A→Z. */
export const buildTree = (files: VaultFile[]): TreeNode[] => {
  const root: TreeNode = { name: "", children: [] };

  for (const file of files) {
    const segments = file.rel.split("/");
    let node = root;
    segments.forEach((segment, index) => {
      const isFile = index === segments.length - 1;
      if (isFile) {
        node.children.push({ name: segment, path: file.path, children: [] });
        return;
      }
      let next = node.children.find((child) => !child.path && child.name === segment);
      if (!next) {
        next = { name: segment, children: [] };
        node.children.push(next);
      }
      node = next;
    });
  }

  const sort = (nodes: TreeNode[]): TreeNode[] => {
    nodes.sort((a, b) => {
      const aIsFile = a.path !== undefined;
      const bIsFile = b.path !== undefined;
      if (aIsFile !== bIsFile) return aIsFile ? 1 : -1;
      return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
    });
    for (const node of nodes) sort(node.children);
    return nodes;
  };

  return sort(root.children);
};
